/**
 * Preset tariffs (rate plans) manager, shown on the Default pricing screen.
 * Each tariff has a name and a credit price per billing channel; a tariff can
 * then be assigned to an account in the pricing matrix. A channel left empty
 * falls back to the platform default. SuperUser-only for mutations.
 */

import React from "react";
import { useNotify, useTranslate } from "react-admin";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputBase,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  DeleteOutline as DeleteIcon,
} from "@mui/icons-material";
import { extractErrorMessage } from "@/utils/extractErrorMessage";
import { useCanEdit } from "@/utils/permissions";
import {
  getPricingMatrix,
  saveTariff,
  deleteTariff,
  type PricingPlan,
} from "../services/pricingMatrixService";

const i18nBase = "resources.credit-consumption/pricing";

interface DialogState {
  open: boolean;
  id?: string;
  name: string;
  prices: Record<string, string>; // lowercase channel -> raw input
}

const emptyDialog = (): DialogState => ({ open: false, name: "", prices: {} });

export const TariffsManager: React.FC = () => {
  const translate = useTranslate();
  const notify = useNotify();
  const theme = useTheme();
  const canEdit = useCanEdit();

  const [channels, setChannels] = React.useState<string[]>([]);
  const [defaults, setDefaults] = React.useState<Record<string, number>>({});
  const [plans, setPlans] = React.useState<PricingPlan[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [dialog, setDialog] = React.useState<DialogState>(emptyDialog());
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(() => {
    setLoading(true);
    getPricingMatrix()
      .then((m) => {
        setChannels(m.channels);
        setDefaults(m.defaults);
        setPlans(m.plans);
      })
      .catch((err: unknown) => {
        notify(
          extractErrorMessage(err, "ra.notification.http_error", translate),
          { type: "error" },
        );
      })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const openCreate = (): void =>
    setDialog({ open: true, name: "", prices: {} });

  const openEdit = (plan: PricingPlan): void => {
    const prices: Record<string, string> = {};
    channels.forEach((ch) => {
      const v = plan.prices[ch.toLowerCase()];
      if (v !== undefined) prices[ch.toLowerCase()] = String(v);
    });
    setDialog({ open: true, id: plan.id, name: plan.name, prices });
  };

  const closeDialog = (): void => setDialog(emptyDialog());

  const commit = async (): Promise<void> => {
    const name = dialog.name.trim();
    if (!name) {
      notify(translate(`${i18nBase}.tariffs.nameRequired`), { type: "warning" });
      return;
    }
    const prices: Record<string, number> = {};
    for (const ch of channels) {
      const raw = (dialog.prices[ch.toLowerCase()] ?? "").trim();
      if (raw === "") continue;
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0) {
        notify(translate(`${i18nBase}.tariffs.invalidPrice`, { channel: ch }), {
          type: "warning",
        });
        return;
      }
      prices[ch] = n;
    }
    setSaving(true);
    try {
      await saveTariff({ id: dialog.id, name, prices });
      notify(translate(`${i18nBase}.tariffs.saved`), { type: "success" });
      closeDialog();
      load();
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const remove = async (plan: PricingPlan): Promise<void> => {
    if (
      !window.confirm(
        translate(`${i18nBase}.tariffs.confirmDelete`, { name: plan.name }),
      )
    )
      return;
    try {
      await deleteTariff(plan.id);
      notify(translate(`${i18nBase}.tariffs.deleted`), { type: "success" });
      load();
    } catch (err: unknown) {
      notify(extractErrorMessage(err, "ra.notification.http_error", translate), {
        type: "error",
      });
    }
  };

  const headerCell = {
    px: 1,
    py: 1,
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    color: theme.palette.text.secondary,
    whiteSpace: "nowrap" as const,
    borderLeft: `1px solid ${theme.palette.divider}`,
  };

  return (
    <Box sx={{ mt: 5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          ml: 2,
          mb: 1,
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {translate(`${i18nBase}.tariffs.title`)}
          </Typography>
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", maxWidth: 900 }}>
            {translate(`${i18nBase}.tariffs.hint`)}
          </Typography>
        </Box>
        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={openCreate}
            sx={{ textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
          >
            {translate(`${i18nBase}.tariffs.add`)}
          </Button>
        )}
      </Box>

      {loading ? (
        <Box sx={{ display: "grid", placeItems: "center", py: 6 }}>
          <CircularProgress size={26} />
        </Box>
      ) : plans.length === 0 ? (
        <Typography sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
          {translate(`${i18nBase}.tariffs.empty`)}
        </Typography>
      ) : (
        <Box
          sx={{
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
            overflow: "auto",
            maxWidth: "100%",
          }}
        >
          <Box
            component="table"
            sx={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}
          >
            <Box component="thead">
              <Box component="tr" sx={{ bgcolor: "action.hover" }}>
                <Box
                  component="th"
                  sx={{
                    ...headerCell,
                    borderLeft: "none",
                    textAlign: "left",
                    position: "sticky",
                    left: 0,
                    bgcolor: theme.palette.background.paper,
                    minWidth: 180,
                  }}
                >
                  {translate(`${i18nBase}.tariffs.name`)}
                </Box>
                {channels.map((ch) => (
                  <Box component="th" key={ch} sx={{ ...headerCell, textAlign: "right" }}>
                    <Tooltip
                      title={`${translate(`${i18nBase}.matrix.default`)}: ${defaults[ch.toLowerCase()] ?? "—"}`}
                    >
                      <span>{ch}</span>
                    </Tooltip>
                  </Box>
                ))}
                {canEdit && (
                  <Box component="th" sx={{ ...headerCell, textAlign: "right" }} />
                )}
              </Box>
            </Box>
            <Box component="tbody">
              {plans.map((plan) => (
                <Box
                  component="tr"
                  key={plan.id}
                  sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
                >
                  <Box
                    component="td"
                    sx={{
                      px: 1.5,
                      py: 0.75,
                      position: "sticky",
                      left: 0,
                      bgcolor: theme.palette.background.paper,
                      borderRight: `1px solid ${theme.palette.divider}`,
                    }}
                  >
                    <Typography sx={{ fontSize: 13, fontWeight: 600, whiteSpace: "nowrap" }}>
                      {plan.name}
                    </Typography>
                  </Box>
                  {channels.map((ch) => {
                    const v = plan.prices[ch.toLowerCase()];
                    const usesDefault = v === undefined;
                    return (
                      <Box
                        component="td"
                        key={ch}
                        sx={{
                          px: 1,
                          py: 0.75,
                          textAlign: "right",
                          borderLeft: `1px solid ${theme.palette.divider}`,
                          fontSize: 13,
                          fontVariantNumeric: "tabular-nums",
                          color: usesDefault ? "text.disabled" : "text.primary",
                          fontWeight: usesDefault ? 400 : 700,
                        }}
                      >
                        {usesDefault
                          ? (defaults[ch.toLowerCase()] ?? "—")
                          : v}
                      </Box>
                    );
                  })}
                  {canEdit && (
                    <Box
                      component="td"
                      sx={{
                        px: 1,
                        py: 0.25,
                        textAlign: "right",
                        borderLeft: `1px solid ${theme.palette.divider}`,
                        whiteSpace: "nowrap",
                      }}
                    >
                      <IconButton size="small" onClick={() => openEdit(plan)} aria-label="edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => void remove(plan)}
                        aria-label="delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        </Box>
      )}

      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          {dialog.id
            ? translate(`${i18nBase}.tariffs.editTitle`)
            : translate(`${i18nBase}.tariffs.createTitle`)}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            label={translate(`${i18nBase}.tariffs.name`)}
            value={dialog.name}
            onChange={(e) => setDialog((d) => ({ ...d, name: e.target.value }))}
            fullWidth
            autoFocus
            sx={{ mb: 2, mt: 1 }}
          />
          <Typography sx={{ fontSize: 12.5, color: "text.secondary", mb: 1 }}>
            {translate(`${i18nBase}.tariffs.pricesHint`)}
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1 }}>
            {channels.map((ch) => (
              <Box
                key={ch}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1,
                  py: 0.5,
                }}
              >
                <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{ch}</Typography>
                <InputBase
                  value={dialog.prices[ch.toLowerCase()] ?? ""}
                  onChange={(e) =>
                    setDialog((d) => ({
                      ...d,
                      prices: { ...d.prices, [ch.toLowerCase()]: e.target.value },
                    }))
                  }
                  type="number"
                  placeholder={String(defaults[ch.toLowerCase()] ?? 0)}
                  inputProps={{
                    min: 0,
                    step: "any",
                    style: { textAlign: "right", width: 70 },
                  }}
                  sx={{ fontSize: 13 }}
                />
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} sx={{ textTransform: "none" }}>
            {translate("ra.action.cancel")}
          </Button>
          <Button
            variant="contained"
            onClick={() => void commit()}
            disabled={saving}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            {saving ? (
              <CircularProgress size={18} />
            ) : (
              translate("ra.action.save")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TariffsManager;
